import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { ArrowUpRight } from "lucide-react";
import { TAGLINE } from "../brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

/**
 * 🤝 PublicLeadForm — single intake widget reused on the Alliances and
 * Employers pages. Posts to /api/inquiries (public, unauthenticated). The
 * `kind` prop drives the inquiry_type stamped on the lead so the SA/CAO
 * dashboard can filter by source. `serviceOptions` controls the "Primary
 * Services Provided" dropdown — Alliance version shows housing / mental
 * health / etc; Employer version stays minimal.
 */
export default function PublicLeadForm({
  kind = "alliance_partnership",
  title = "Apply for Alliance Partnership",
  subtitle = "Tell us about your organization and how we can collaborate.",
  serviceOptions = [
    { value: "housing",            label: "Housing" },
    { value: "mental_health",      label: "Mental Health" },
    { value: "substance_support",  label: "Substance Support" },
    { value: "food_security",      label: "Food Security" },
    { value: "other",              label: "Other" },
  ],
  accent = "#C9A227",
}) {
  const [form, setForm] = useState({
    organization: "", director_name: "", email: "", phone: "",
    service_area: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization || !form.director_name || !form.email) {
      toast.error("Please fill Organization, Director, and Email.");
      return;
    }
    if (!form.message || form.message.trim().length < 10) {
      toast.error("Please describe your collaboration goals (10+ characters).");
      return;
    }
    setLoading(true);
    try {
      // /api/inquiries is the legacy lead endpoint that already feeds the
      // Inquiries Inbox; we extended it server-side to accept director_name,
      // phone, and service_area plus the alliance_partnership inquiry_type.
      // role is set to "Director" so the model's min-length 2 validator
      // is satisfied without exposing a redundant field to the public user.
      await axios.post(`${API}/inquiries`, {
        organization: form.organization,
        name: form.director_name,
        director_name: form.director_name,
        email: form.email,
        phone: form.phone,
        role: "Director",
        service_area: form.service_area || null,
        inquiry_type: kind,
        message: form.message,
      }, { withCredentials: false });
      setSubmitted(true);
      toast.success("Received. The Alliance will respond within 2 business days.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please try again.");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div data-testid="public-lead-form-success" className="bg-white border border-[#E8E5DA] p-10 sm:p-12 shadow-sm" style={{ borderTop: `3px solid ${accent}` }}>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase mb-3" style={{ color: accent }}>📨 Application Received</div>
        <h3 className="font-display font-bold text-3xl tracking-tight text-[#0A0612] mb-3">Thank you, {form.director_name}.</h3>
        <p className="text-[#6B6F76] text-base max-w-xl">Your submission for <b>{form.organization}</b> is now in the Alliance's intake queue.
          A member of our Executive team will review and reach out to <b>{form.email}</b> within two business days.</p>
        <p className="text-[#6B6F76] text-sm mt-4 font-mono tracking-[0.04em]">Reference: {TAGLINE}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="public-lead-form" className="bg-white border border-[#E8E5DA] p-6 sm:p-10 shadow-sm" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="font-mono text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: accent }}>Secure Public Intake</div>
      <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#0A0612] mb-2">{title}</h3>
      <p className="text-[#6B6F76] text-sm sm:text-base mb-7">{subtitle}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div className="sm:col-span-2">
          <Label htmlFor="org" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-1.5 block">Organization Name *</Label>
          <Input id="org" data-testid="lead-organization" value={form.organization} onChange={set("organization")} required maxLength={160} className="rounded-none bg-white border-[#E8E5DA] h-11 focus-visible:border-[#0A0612] focus-visible:ring-0"/>
        </div>
        <div>
          <Label htmlFor="director" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-1.5 block">Director Name *</Label>
          <Input id="director" data-testid="lead-director" value={form.director_name} onChange={set("director_name")} required maxLength={120} className="rounded-none bg-white border-[#E8E5DA] h-11 focus-visible:border-[#0A0612] focus-visible:ring-0"/>
        </div>
        <div>
          <Label htmlFor="email" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-1.5 block">Contact Email *</Label>
          <Input id="email" type="email" data-testid="lead-email" value={form.email} onChange={set("email")} required className="rounded-none bg-white border-[#E8E5DA] h-11 focus-visible:border-[#0A0612] focus-visible:ring-0"/>
        </div>
        <div>
          <Label htmlFor="phone" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-1.5 block">Phone</Label>
          <Input id="phone" type="tel" data-testid="lead-phone" value={form.phone} onChange={set("phone")} maxLength={40} className="rounded-none bg-white border-[#E8E5DA] h-11 focus-visible:border-[#0A0612] focus-visible:ring-0"/>
        </div>
        <div>
          <Label htmlFor="service" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-1.5 block">Primary Service Provided</Label>
          <select id="service" data-testid="lead-service" value={form.service_area} onChange={set("service_area")} className="w-full rounded-none bg-white border border-[#E8E5DA] h-11 px-3 font-display text-[#0A0612] focus:outline-none focus:border-[#0A0612]">
            <option value="">— Select one —</option>
            {serviceOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="message" className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#6B6F76] mb-1.5 block">Message / Collaboration Goals *</Label>
          <Textarea id="message" data-testid="lead-message" value={form.message} onChange={set("message")} required rows={5} minLength={10} maxLength={4000} placeholder="Tell us about your mission, the populations you serve, and how you envision linking into the Alliance network…" className="rounded-none bg-white border-[#E8E5DA] focus-visible:border-[#0A0612] focus-visible:ring-0"/>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-7">
        <p className="text-xs text-[#6B6F76]">🔒 Encrypted in transit · Reviewed by the CAO within 2 business days</p>
        <Button type="submit" disabled={loading} data-testid="lead-submit" className="rounded-none bg-[#0A0612] hover:bg-[#1A1A1A] text-[#F4D88A] font-mono text-[11px] tracking-[0.22em] uppercase h-11 px-6 ring-1 ring-[#D4AF37]/40 disabled:opacity-50">
          {loading ? "Submitting…" : <>Submit Application <ArrowUpRight className="h-4 w-4 ml-2"/></>}
        </Button>
      </div>
    </form>
  );
}
